/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Card,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Icon,
  CircularProgress,
  Button,
} from "@mui/material";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

// Layout container
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";

// Redux Thunks
import { getLeadByChartsThunk } from "redux/Thunks/LoanFormConfig";
import { getInquiryStatsThunk } from "redux/Thunks/Inquiry";

// Register ChartJS modules
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const monthOptions = [
  { value: "", label: "All Months" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
];

function NewDashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [inquiryStats, setInquiryStats] = useState({
    total: 0,
    pending: 0,
    processed: 0,
    rejected: 0,
    pendingAmount: 0,
    processedAmount: 0,
  });

  // Dynamic helper for Current Financial Year
  const getCurrentFY = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const fyStart = month >= 4 ? year : year - 1;
    return `${fyStart}-${String(fyStart + 1).slice(-2)}`;
  };

  // Single unified state for Financial Year & Month
  const [selectedYear, setSelectedYear] = useState(getCurrentFY());
  const [selectedMonth, setSelectedMonth] = useState("");

  const fyOptions = useMemo(() => {
    const now = new Date();
    const currentFYStart = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;
    const list = [];
    for (let y = currentFYStart + 1; y >= currentFYStart - 3; y--) {
      list.push(`${y}-${String(y + 1).slice(-2)}`);
    }
    if (!list.includes("2026-27")) list.push("2026-27");
    if (!list.includes("2025-26")) list.push("2025-26");
    return list;
  }, []);

  // Fetch inquiry stats
  const fetchInquiryStats = async () => {
    try {
      const res = await dispatch(getInquiryStatsThunk());
      if (res?.payload?.data) {
        setInquiryStats(res.payload.data);
      }
    } catch (err) {
      console.error("Failed to fetch inquiry stats:", err);
    }
  };

  useEffect(() => {
    fetchInquiryStats();
  }, []);

  // Fetch real API data whenever selectedYear or selectedMonth changes
  const fetchDashboardData = async (yearStr, monthVal) => {
    setLoading(true);
    const yearNum = yearStr ? yearStr.split("-")[0] : new Date().getFullYear();
    let queryParam = `year=${yearNum}`;
    if (monthVal) {
      queryParam += `&month=${monthVal}`;
    }
    const res = await dispatch(getLeadByChartsThunk(queryParam));
    if (res.payload?.status === 200 && res.payload?.data?.data) {
      setDashboardData(res.payload.data.data);
    } else {
      setDashboardData(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchDashboardData(selectedYear, selectedMonth);
  }, [selectedYear, selectedMonth]);

  // Helper formatter for Currency Amounts
  const formatAmount = (num) => {
    if (num === null || num === undefined) return "₹ 0.00";
    if (num >= 10000000) return `₹ ${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `₹ ${(num / 100000).toFixed(2)} L`;
    return `₹ ${num.toLocaleString()}`;
  };

  // Helper icon for product types
  const getProductIcon = (prod) => {
    const type = prod?.type || prod;
    switch (type) {
      case "personal":
        return { icon: "person", bgColor: "#1570EF" };
      case "business":
        return { icon: "storefront", bgColor: "#12B76A" };
      case "credit":
      case "credit card":
        return { icon: "credit_card", bgColor: "#9E77ED" };
      case "home":
        return { icon: "home", bgColor: "#1570EF" };
      case "loanAgainstProperty":
        return { icon: "apartment", bgColor: "#06AED4" };
      case "car":
        return { icon: "directions_car", bgColor: "#F04438" };
      case "education":
        return { icon: "school", bgColor: "#6172F3" };
      case "loanAgainstSecurity":
        return { icon: "shield", bgColor: "#475467" };
      case "workingCapital":
        return { icon: "account_balance", bgColor: "#0284C7" };
      case "machinery":
        return { icon: "precision_manufacturing", bgColor: "#E63F59" };
      case "gold":
        return { icon: "workspace_premium", bgColor: "#F79009" };
      default:
        return { icon: "account_balance", bgColor: "#0284C7" };
    }
  };

  const DEFAULT_PRODUCTS = [
    // UNSECURED
    {
      type: "credit",
      title: "Credit Card (CC)",
      subtitle: "Consumer Credit Facility",
      category: "unsecured",
      icon: "credit_card",
      iconBg: "#9E77ED",
      totalLeads: 0,
      sanctionedLeads: 0,
      disbursementLeads: 0,
      disbursementAmount: 0,
    },
    {
      type: "personal",
      title: "Personal Loan (PL)",
      subtitle: "Unsecured Consumer Lending",
      category: "unsecured",
      icon: "person",
      iconBg: "#1570EF",
      totalLeads: 0,
      sanctionedLeads: 0,
      disbursementLeads: 0,
      disbursementAmount: 0,
    },
    {
      type: "business",
      title: "Business Loan (BL)",
      subtitle: "MSME & Commercial Lending",
      category: "unsecured",
      icon: "storefront",
      iconBg: "#12B76A",
      totalLeads: 0,
      sanctionedLeads: 0,
      disbursementLeads: 0,
      disbursementAmount: 0,
    },

    // SECURED
    {
      type: "home",
      title: "Home Loan (HL)",
      subtitle: "Mortgage & Residential Finance",
      category: "secured",
      icon: "home",
      iconBg: "#1570EF",
      totalLeads: 0,
      sanctionedLeads: 0,
      disbursementLeads: 0,
      disbursementAmount: 0,
    },
    {
      type: "loanAgainstProperty",
      title: "Loan Against Property (LAP)",
      subtitle: "Commercial & Residential Mortgage",
      category: "secured",
      icon: "apartment",
      iconBg: "#06AED4",
      totalLeads: 0,
      sanctionedLeads: 0,
      disbursementLeads: 0,
      disbursementAmount: 0,
    },
    {
      type: "car",
      title: "Car Loan (CL)",
      subtitle: "Auto Finance",
      category: "secured",
      icon: "directions_car",
      iconBg: "#F04438",
      totalLeads: 0,
      sanctionedLeads: 0,
      disbursementLeads: 0,
      disbursementAmount: 0,
    },
    {
      type: "education",
      title: "Education Loan (EL)",
      subtitle: "Higher Studies Finance",
      category: "secured",
      icon: "school",
      iconBg: "#6172F3",
      totalLeads: 0,
      sanctionedLeads: 0,
      disbursementLeads: 0,
      disbursementAmount: 0,
    },
    {
      type: "loanAgainstSecurity",
      title: "Loan Against Security (LAS)",
      subtitle: "Securities & Portfolio Credit",
      category: "secured",
      icon: "shield",
      iconBg: "#475467",
      totalLeads: 0,
      sanctionedLeads: 0,
      disbursementLeads: 0,
      disbursementAmount: 0,
    },
    {
      type: "workingCapital",
      title: "Working Capital (WC)",
      subtitle: "Business Working Capital & Cashflow",
      category: "secured",
      icon: "account_balance",
      iconBg: "#0284C7",
      totalLeads: 0,
      sanctionedLeads: 0,
      disbursementLeads: 0,
      disbursementAmount: 0,
    },
    {
      type: "machinery",
      title: "Machinery Loan (ML)",
      subtitle: "Equipment & Machinery Finance",
      category: "secured",
      icon: "precision_manufacturing",
      iconBg: "#E63F59",
      totalLeads: 0,
      sanctionedLeads: 0,
      disbursementLeads: 0,
      disbursementAmount: 0,
    },
    {
      type: "gold",
      title: "Gold Loan (GL)",
      subtitle: "Gold Asset Finance",
      category: "secured",
      icon: "workspace_premium",
      iconBg: "#F79009",
      totalLeads: 0,
      sanctionedLeads: 0,
      disbursementLeads: 0,
      disbursementAmount: 0,
    },
  ];

  // Process Line Chart Data
  const fyPerf = dashboardData?.financialYearPerformance || [];
  const chartLabels = [
    "April",
    "May",
    "Jun",
    "July",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
    "Jan",
    "Feb",
    "Mar",
  ];

  const selectedMonthChartIndex = useMemo(() => {
    if (!selectedMonth) return -1;
    const monthNum = parseInt(selectedMonth, 10);
    if (isNaN(monthNum)) return -1;
    return monthNum >= 4 ? monthNum - 4 : monthNum + 8;
  }, [selectedMonth]);

  const selectedMonthPlugin = useMemo(
    () => ({
      id: "selectedMonthLine",
      afterDatasetsDraw: (chart) => {
        if (selectedMonthChartIndex < 0) return;
        const { ctx, chartArea } = chart;
        const meta = chart.getDatasetMeta(0);
        if (!meta || !meta.data || !meta.data[selectedMonthChartIndex]) return;

        const xPos = meta.data[selectedMonthChartIndex].x;
        if (xPos === undefined || xPos === null) return;

        ctx.save();
        ctx.beginPath();
        ctx.setLineDash([5, 5]);
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = "#53B1FD";
        ctx.moveTo(xPos, chartArea.top);
        ctx.lineTo(xPos, chartArea.bottom);
        ctx.stroke();
        ctx.restore();
      },
    }),
    [selectedMonthChartIndex]
  );

  const leadCountData = fyPerf.map((d) => d.leadCount ?? 0);
  const disbursementAmtData = fyPerf.map((d) =>
    d.disbursementAmount ? d.disbursementAmount / 100000 : 0
  ); // In Lakhs for chart scale

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        label: "Lead Count",
        data: leadCountData,
        yAxisID: "y",
        borderColor: "#12B76A",
        backgroundColor: (context) => {
          const { ctx } = context.chart;
          const gradient = ctx.createLinearGradient(0, 0, 0, 300);
          gradient.addColorStop(0, "rgba(18, 183, 106, 0.25)");
          gradient.addColorStop(1, "rgba(18, 183, 106, 0.0)");
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointRadius: (ctx) => (ctx.dataIndex === selectedMonthChartIndex ? 7 : 4),
        pointBackgroundColor: "#12B76A",
        pointBorderColor: "#FFFFFF",
        pointBorderWidth: (ctx) => (ctx.dataIndex === selectedMonthChartIndex ? 3 : 2),
        pointHoverRadius: (ctx) => (ctx.dataIndex === selectedMonthChartIndex ? 9 : 6),
        borderWidth: 2.5,
      },
      {
        label: "Disbursement Amount",
        data: disbursementAmtData,
        yAxisID: "y1",
        borderColor: "#2E90FA",
        backgroundColor: (context) => {
          const { ctx } = context.chart;
          const gradient = ctx.createLinearGradient(0, 0, 0, 300);
          gradient.addColorStop(0, "rgba(46, 144, 250, 0.25)");
          gradient.addColorStop(1, "rgba(46, 144, 250, 0.0)");
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointRadius: (ctx) => (ctx.dataIndex === selectedMonthChartIndex ? 7 : 4),
        pointBackgroundColor: "#2E90FA",
        pointBorderColor: "#FFFFFF",
        pointBorderWidth: (ctx) => (ctx.dataIndex === selectedMonthChartIndex ? 3 : 2),
        pointHoverRadius: (ctx) => (ctx.dataIndex === selectedMonthChartIndex ? 9 : 6),
        borderWidth: 2.5,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "#1D2939",
        titleFont: { size: 13, weight: "600" },
        bodyFont: { size: 12 },
        padding: 10,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: (context) => {
            const datasetLabel = context.dataset.label || "";
            const val = context.parsed.y;
            if (val === null || val === undefined) return "";
            if (context.datasetIndex === 0) {
              return ` ${datasetLabel}: ${val.toLocaleString()} leads`;
            }
            if (val >= 100) {
              return ` ${datasetLabel}: ₹ ${(val / 100).toFixed(
                2
              )} Cr (₹ ${val.toLocaleString()} L)`;
            }
            return ` ${datasetLabel}: ₹ ${val.toFixed(2)} L`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: "#667085",
          font: { size: 12, weight: "500" },
        },
      },
      y: {
        type: "linear",
        display: true,
        position: "left",
        grid: {
          color: "#F2F4F7",
          drawBorder: false,
        },
        ticks: {
          color: "#667085",
          font: { size: 12, weight: "500" },
          callback: (value) => (Number.isInteger(value) ? `${value}` : ""),
        },
        min: 0,
        title: {
          display: true,
          text: "Leads",
          color: "#12B76A",
          font: { size: 12, weight: "600" },
        },
      },
      y1: {
        type: "linear",
        display: true,
        position: "right",
        grid: {
          drawOnChartArea: false,
          drawBorder: false,
        },
        ticks: {
          color: "#667085",
          font: { size: 12, weight: "500" },
          callback: (value) => {
            if (value === 0) return "₹ 0";
            if (value >= 100) return `₹ ${(value / 100).toFixed(1)} Cr`;
            return `₹ ${value} L`;
          },
        },
        min: 0,
        title: {
          display: true,
          text: "Disbursement",
          color: "#2E90FA",
          font: { size: 12, weight: "600" },
        },
      },
    },
  };

  // Card shadow & border styling
  const cardStyle = {
    p: 3,
    borderRadius: "16px",
    boxShadow: "0 1px 3px rgba(16, 24, 40, 0.08), 0 1px 2px rgba(16, 24, 40, 0.04)",
    border: "1px solid #EAECF0",
    backgroundColor: "#FFFFFF",
    height: "100%",
  };

  const fieldBoxStyle = {
    p: 1.5,
    borderRadius: "10px",
    backgroundColor: "#F9FAFB",
    border: "1px solid #F2F4F7",
    height: "100%",
    minHeight: "78px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  };

  const fieldLabelStyle = {
    fontSize: "12px",
    fontWeight: "500",
    color: "#667085",
    lineHeight: 1.25,
    minHeight: "28px",
    display: "flex",
    alignItems: "center",
  };

  const fieldValueStyle = {
    fontSize: "18px",
    fontWeight: "700",
    color: "#101828",
    lineHeight: 1.2,
    mt: 0.5,
  };

  const clickableCardStyle = {
    ...cardStyle,
    cursor: "pointer",
    transition: "transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(16, 24, 40, 0.08)",
    },
  };

  const clickableFieldBoxStyle = {
    ...fieldBoxStyle,
    cursor: "pointer",
    transition:
      "background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, transform 0.15s ease-in-out",
    "&:hover": {
      backgroundColor: "#F2F4F7",
      borderColor: "#D0D5DD",
      transform: "translateY(-1px)",
    },
  };

  // Custom Select dropdown styling
  const dropdownSx = {
    height: "38px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "500",
    color: "#344054",
    backgroundColor: "#FFFFFF",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#D0D5DD",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#98A2B3",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#1570EF",
    },
    "& .MuiSelect-select": {
      py: "8px",
      px: "12px",
      display: "flex",
      alignItems: "center",
    },
  };

  // Dynamic values directly from API response
  const yearlyOverview = dashboardData?.yearlyOverview || {};

  const selectedMonthObj = monthOptions.find((m) => m.value === selectedMonth);
  const isMonthSelected = Boolean(selectedMonth && selectedMonthObj?.value);
  const selectedMonthLabel = selectedMonthObj?.label || "";

  // Merge API productWiseAnalysis with DEFAULT_PRODUCTS to guarantee all 11 products in order
  const rawProducts = dashboardData?.productWiseAnalysis || [];
  const allProducts = DEFAULT_PRODUCTS.map((def) => {
    const found = rawProducts.find(
      (p) =>
        p.type === def.type ||
        (def.type === "credit" && (p.type === "credit" || p.type === "credit card"))
    );
    if (found) {
      return {
        ...def,
        ...found,
        totalLeads: found.totalLeads ?? 0,
        sanctionedLeads: found.sanctionedLeads ?? 0,
        disbursementLeads: found.disbursementLeads ?? 0,
        disbursementAmount: found.disbursementAmount ?? 0,
      };
    }
    return def;
  });

  const unsecuredProducts = allProducts.filter((p) => p.category === "unsecured");
  const securedProducts = allProducts.filter((p) => p.category === "secured");

  const getCardGridProps = (index, total) => {
    const xs = 12;

    // Tablet (2 cards per row): if odd total, the last card spans full width (12)
    let sm = 6;
    if (total % 2 === 1 && index === total - 1) {
      sm = 12;
    }

    // Laptop (3 cards per row):
    let lg = 4;
    const remainder3 = total % 3;
    if (remainder3 === 1 && index === total - 1) {
      // 1 card left in last row -> span full width (12)
      lg = 12;
    } else if (remainder3 === 2 && index >= total - 2) {
      // 2 cards left in last row -> split evenly across the row (6 each)
      lg = 6;
    }

    return { xs, sm, lg };
  };

  const renderProductCard = (prod, index, total) => {
    const prodStyle = getProductIcon(prod);
    const isCreditCard =
      prod.type === "credit" ||
      prod.type === "credit card" ||
      prod.title?.toLowerCase().includes("credit card");

    const gridProps =
      typeof total === "number" && total > 0
        ? getCardGridProps(index, total)
        : { xs: 12, sm: 6, lg: 4 };

    return (
      <Grid item {...gridProps} key={prod.type || prod.title}>
        <Card sx={cardStyle}>
          <Box display="flex" alignItems="center" gap={2} mb={2.5}>
            <Box
              width={44}
              height={44}
              borderRadius="10px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{ backgroundColor: prod.iconBg || prodStyle.bgColor }}
            >
              <Icon sx={{ color: "#FFFFFF", fontSize: "22px !important" }}>
                {prod.icon || prodStyle.icon}
              </Icon>
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="700" color="#101828" fontSize="16px">
                {prod.title}
              </Typography>
              <Typography variant="caption" color="#667085" fontSize="12px">
                {prod.subtitle}
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2}>
            {/* Total Leads */}
            <Grid item xs={6}>
              <Box
                sx={clickableFieldBoxStyle}
                onClick={() =>
                  navigate("/client/lead-bay", {
                    state: {
                      financialYear: selectedYear,
                      month: selectedMonth,
                      type: prod.title,
                      currentStatus: "all",
                    },
                  })
                }
              >
                <Typography sx={fieldLabelStyle}>Total Leads</Typography>
                <Typography sx={fieldValueStyle}>
                  {(prod.totalLeads ?? 0).toLocaleString()}
                </Typography>
              </Box>
            </Grid>

            {/* Sanctioned Leads */}
            <Grid item xs={6}>
              <Box
                sx={clickableFieldBoxStyle}
                onClick={() =>
                  navigate("/client/lead-bay", {
                    state: {
                      financialYear: selectedYear,
                      month: selectedMonth,
                      type: prod.title,
                      currentStatus: "Loan Sanctioned",
                    },
                  })
                }
              >
                <Typography sx={fieldLabelStyle}>Sanctioned Leads</Typography>
                <Typography sx={fieldValueStyle}>
                  {(prod.sanctionedLeads ?? 0).toLocaleString()}
                </Typography>
              </Box>
            </Grid>

            {/* Disbursement Leads */}
            <Grid item xs={isCreditCard ? 12 : 6}>
              <Box
                sx={clickableFieldBoxStyle}
                onClick={() =>
                  navigate("/client/lead-bay", {
                    state: {
                      financialYear: selectedYear,
                      month: selectedMonth,
                      type: prod.title,
                      currentStatus: "Amount Credited/Cheque issued",
                    },
                  })
                }
              >
                <Typography sx={fieldLabelStyle}>Disbursement Leads</Typography>
                <Typography sx={fieldValueStyle}>
                  {(prod.disbursementLeads ?? 0).toLocaleString()}
                </Typography>
              </Box>
            </Grid>

            {/* Disbursement Amount */}
            {!isCreditCard && (
              <Grid item xs={6}>
                <Box
                  sx={clickableFieldBoxStyle}
                  onClick={() =>
                    navigate("/client/lead-bay", {
                      state: {
                        financialYear: selectedYear,
                        month: selectedMonth,
                        type: prod.title,
                        currentStatus: "Amount Credited/Cheque issued",
                      },
                    })
                  }
                >
                  <Typography sx={fieldLabelStyle}>Disbursement Amount</Typography>
                  <Typography sx={fieldValueStyle}>
                    {formatAmount(prod.disbursementAmount)}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Card>
      </Grid>
    );
  };

  return (
    <DashboardLayout>
      <MDBox py={3} px={1} sx={{ backgroundColor: "#F8FAFC", minHeight: "100vh" }}>
        {loading && (
          <Box display="flex" justifyContent="center" my={2}>
            <CircularProgress size={28} sx={{ color: "#1570EF" }} />
          </Box>
        )}

        {/* SECTION 1: Yearly / Monthly Overview */}
        <Box mb={4}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            flexDirection={{ xs: "column", sm: "row" }}
            mb={2.5}
            gap={1.5}
          >
            <Box>
              <Typography variant="h5" fontWeight="700" color="#101828" fontSize="22px">
                {isMonthSelected
                  ? `Monthly Overview - ${selectedMonthLabel} (FY ${selectedYear})`
                  : `Yearly Overview ${selectedYear}`}
              </Typography>
              <Typography variant="body2" color="#667085" fontSize="14px" mt={0.5}>
                {isMonthSelected
                  ? `Aggregated performance across all product lines for ${selectedMonthLabel}`
                  : "Aggregated performance across all product Lines"}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              {/* Financial Year Selector */}
              <FormControl size="small">
                <Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  sx={dropdownSx}
                >
                  {fyOptions.map((fy) => (
                    <MenuItem key={fy} value={fy}>
                      Financial Year {fy}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Month Selector */}
              <FormControl size="small">
                <Select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  displayEmpty
                  sx={dropdownSx}
                >
                  {monthOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* View Lead Bay List Button */}
              <Button
                variant="outlined"
                onClick={() =>
                  navigate("/client/lead-bay", {
                    state: {
                      financialYear: selectedYear,
                      month: selectedMonth,
                      currentStatus: "all",
                    },
                  })
                }
                endIcon={<Icon sx={{ fontSize: "18px !important" }}>arrow_forward</Icon>}
                sx={{
                  textTransform: "none",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#0047B3",
                  borderColor: "#D0D5DD",
                  backgroundColor: "#FFFFFF",
                  borderRadius: "8px",
                  px: 2,
                  py: 0.8,
                  "&:hover": {
                    borderColor: "#0047B3",
                    backgroundColor: "#F0F5FF",
                  },
                }}
              >
                View Lead List
              </Button>
            </Box>
          </Box>

          <Grid container spacing={3}>
            {/* Total Leads Card */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={clickableCardStyle}
                onClick={() =>
                  navigate("/client/lead-bay", {
                    state: {
                      financialYear: selectedYear,
                      month: selectedMonth,
                      currentStatus: "all",
                    },
                  })
                }
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <Box
                    width={48}
                    height={48}
                    borderRadius="12px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ backgroundColor: "#EFF8FF" }}
                  >
                    <Icon sx={{ color: "#2E90FA", fontSize: "24px !important" }}>groups</Icon>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="#667085" fontSize="13px" fontWeight="500">
                      Total Leads
                    </Typography>
                    <Typography
                      variant="h4"
                      fontWeight="700"
                      color="#101828"
                      fontSize="26px"
                      mt={0.2}
                    >
                      {(yearlyOverview.totalLeads ?? 0).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            {/* Total Disbursement Count Card */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={clickableCardStyle}
                onClick={() =>
                  navigate("/client/lead-bay", {
                    state: {
                      financialYear: selectedYear,
                      month: selectedMonth,
                      currentStatus: "Amount Credited/Cheque issued",
                    },
                  })
                }
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <Box
                    width={48}
                    height={48}
                    borderRadius="12px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ backgroundColor: "#ECFDF3" }}
                  >
                    <Icon sx={{ color: "#12B76A", fontSize: "24px !important" }}>check_circle</Icon>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="#667085" fontSize="13px" fontWeight="500">
                      Total Disbursement Count
                    </Typography>
                    <Typography
                      variant="h4"
                      fontWeight="700"
                      color="#101828"
                      fontSize="26px"
                      mt={0.2}
                    >
                      {(yearlyOverview.totalDisbursementCount ?? 0).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            {/* Total Disbursement Amount Card */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={clickableCardStyle}
                onClick={() =>
                  navigate("/client/lead-bay", {
                    state: {
                      financialYear: selectedYear,
                      month: selectedMonth,
                      currentStatus: "Amount Credited/Cheque issued",
                    },
                  })
                }
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <Box
                    width={48}
                    height={48}
                    borderRadius="12px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ backgroundColor: "#FEF0C7" }}
                  >
                    <Icon sx={{ color: "#DC6803", fontSize: "24px !important" }}>payments</Icon>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="#667085" fontSize="13px" fontWeight="500">
                      Total Disbursement Amount
                    </Typography>
                    <Typography
                      variant="h4"
                      fontWeight="700"
                      color="#101828"
                      fontSize="26px"
                      mt={0.2}
                    >
                      {formatAmount(yearlyOverview.totalDisbursementAmount)}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* SECTION: Inquiries Tracking */}
        <Box mb={4}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            flexDirection={{ xs: "column", sm: "row" }}
            mb={2.5}
            gap={1.5}
          >
            <Box>
              <Typography variant="h5" fontWeight="700" color="#101828" fontSize="22px">
                Inquiries Tracking
              </Typography>
              <Typography variant="body2" color="#667085" fontSize="14px" mt={0.5}>
                Monitor pending customer inquiries and tracked processed applications
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={() => navigate("/client/inquiries")}
              endIcon={<Icon sx={{ fontSize: "18px !important" }}>arrow_forward</Icon>}
              sx={{
                textTransform: "none",
                fontSize: "13px",
                fontWeight: 600,
                color: "#0047B3",
                borderColor: "#D0D5DD",
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                px: 2,
                py: 0.8,
                "&:hover": {
                  borderColor: "#0047B3",
                  backgroundColor: "#F0F5FF",
                },
              }}
            >
              View Inquiries List
            </Button>
          </Box>

          <Grid container spacing={3}>
            {/* Pending Inquiries Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  ...cardStyle,
                  cursor: "pointer",
                  transition: "transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(16, 24, 40, 0.08)",
                  },
                }}
                onClick={() => navigate("/client/inquiries", { state: { status: "pending" } })}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <Box
                    width={48}
                    height={48}
                    borderRadius="12px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ backgroundColor: "#FEF5CD" }}
                  >
                    <Icon sx={{ color: "#DA850A", fontSize: "24px !important" }}>
                      pending_actions
                    </Icon>
                  </Box>
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography
                        variant="caption"
                        color="#667085"
                        fontSize="13px"
                        fontWeight="500"
                      >
                        Pending Inquiries
                      </Typography>
                      <Box
                        component="span"
                        sx={{
                          backgroundColor: "#FEF5CD",
                          color: "#DA850A",
                          px: 1,
                          py: 0.25,
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: 600,
                        }}
                      >
                        Needs Action
                      </Box>
                    </Box>
                    <Typography
                      variant="h4"
                      fontWeight="700"
                      color="#101828"
                      fontSize="26px"
                      mt={0.2}
                    >
                      {(inquiryStats.pending ?? 0).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="#667085" fontSize="12px">
                      Editable &amp; awaiting loan processing
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            {/* Processed Inquiries Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  ...cardStyle,
                  cursor: "pointer",
                  transition: "transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(16, 24, 40, 0.08)",
                  },
                }}
                onClick={() => navigate("/client/inquiries", { state: { status: "processed" } })}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <Box
                    width={48}
                    height={48}
                    borderRadius="12px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ backgroundColor: "#DBF4DB" }}
                  >
                    <Icon sx={{ color: "#28873C", fontSize: "24px !important" }}>task_alt</Icon>
                  </Box>
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography
                        variant="caption"
                        color="#667085"
                        fontSize="13px"
                        fontWeight="500"
                      >
                        Processed Inquiries
                      </Typography>
                      <Box
                        component="span"
                        sx={{
                          backgroundColor: "#DBF4DB",
                          color: "#28873C",
                          px: 1,
                          py: 0.25,
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: 600,
                        }}
                      >
                        Processed
                      </Box>
                    </Box>
                    <Typography
                      variant="h4"
                      fontWeight="700"
                      color="#101828"
                      fontSize="26px"
                      mt={0.2}
                    >
                      {(inquiryStats.processed ?? 0).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="#667085" fontSize="12px">
                      Applied into Lead Bay
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            {/* Rejected Inquiries Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  ...cardStyle,
                  cursor: "pointer",
                  transition: "transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(16, 24, 40, 0.08)",
                  },
                }}
                onClick={() => navigate("/client/inquiries", { state: { status: "rejected" } })}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <Box
                    width={48}
                    height={48}
                    borderRadius="12px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ backgroundColor: "#FDE8E8" }}
                  >
                    <Icon sx={{ color: "#E02424", fontSize: "24px !important" }}>cancel</Icon>
                  </Box>
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography
                        variant="caption"
                        color="#667085"
                        fontSize="13px"
                        fontWeight="500"
                      >
                        Rejected Inquiries
                      </Typography>
                      <Box
                        component="span"
                        sx={{
                          backgroundColor: "#FDE8E8",
                          color: "#E02424",
                          px: 1,
                          py: 0.25,
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: 600,
                        }}
                      >
                        Rejected
                      </Box>
                    </Box>
                    <Typography
                      variant="h4"
                      fontWeight="700"
                      color="#101828"
                      fontSize="26px"
                      mt={0.2}
                    >
                      {(inquiryStats.rejected ?? 0).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="#667085" fontSize="12px">
                      Bank, customer &amp; owner
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            {/* Total Inquiries Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  ...cardStyle,
                  cursor: "pointer",
                  transition: "transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(16, 24, 40, 0.08)",
                  },
                }}
                onClick={() => navigate("/client/inquiries", { state: { status: "all" } })}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <Box
                    width={48}
                    height={48}
                    borderRadius="12px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ backgroundColor: "#EFF8FF" }}
                  >
                    <Icon sx={{ color: "#1570EF", fontSize: "24px !important" }}>
                      contact_support
                    </Icon>
                  </Box>
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography
                        variant="caption"
                        color="#667085"
                        fontSize="13px"
                        fontWeight="500"
                      >
                        Total Inquiries
                      </Typography>
                      <Box
                        component="span"
                        sx={{
                          backgroundColor: "#EFF8FF",
                          color: "#1570EF",
                          px: 1,
                          py: 0.25,
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: 600,
                        }}
                      >
                        Total
                      </Box>
                    </Box>
                    <Typography
                      variant="h4"
                      fontWeight="700"
                      color="#101828"
                      fontSize="26px"
                      mt={0.2}
                    >
                      {(inquiryStats.total ?? 0).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="#667085" fontSize="12px">
                      Overall inquiries received
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* SECTION 2: Financial Year Performance Chart */}
        <Box mb={4}>
          <Card sx={cardStyle}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              flexDirection={{ xs: "column", sm: "row" }}
              mb={3}
              gap={2}
            >
              <Box>
                <Typography variant="h6" fontWeight="700" color="#101828" fontSize="18px">
                  Financial Year Performance
                </Typography>
                <Typography variant="body2" color="#667085" fontSize="13px" mt={0.3}>
                  Lead Count &amp; Disbursement Performance{" "}
                  {isMonthSelected ? `- ${selectedMonthLabel}` : ""} (FY {selectedYear})
                </Typography>
              </Box>

              <Box display="flex" alignItems="center" gap={3} flexWrap="wrap">
                {/* Custom Legends */}
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    width={10}
                    height={10}
                    borderRadius="50%"
                    sx={{ backgroundColor: "#12B76A" }}
                  />
                  <Typography variant="caption" color="#344054" fontSize="13px" fontWeight="500">
                    Lead Count
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    width={10}
                    height={10}
                    borderRadius="50%"
                    sx={{ backgroundColor: "#2E90FA" }}
                  />
                  <Typography variant="caption" color="#344054" fontSize="13px" fontWeight="500">
                    Disbursement Amount
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box height={320} width="100%">
              <Line
                data={chartData}
                options={chartOptions}
                plugins={selectedMonthChartIndex >= 0 ? [selectedMonthPlugin] : []}
              />
            </Box>
          </Card>
        </Box>

        {/* SECTION 3: Product-wise Analysis */}
        <Box mb={4}>
          <Box mb={2.5}>
            <Typography variant="h6" fontWeight="700" color="#101828" fontSize="18px">
              Product-wise Analysis
            </Typography>
            <Typography variant="body2" color="#667085" fontSize="13px" mt={0.3}>
              {isMonthSelected
                ? `Compare loan product performance for ${selectedMonthLabel} (FY ${selectedYear}).`
                : "Compare loan product performance and annual target achievement."}
            </Typography>
          </Box>

          {/* Category 1: UNSECURED */}
          <Box mb={3.5}>
            <Typography
              variant="subtitle2"
              fontWeight="700"
              color="#101828"
              fontSize="14px"
              sx={{ textTransform: "uppercase", letterSpacing: "0.5px", mb: 2 }}
            >
              UNSECURED
            </Typography>
            <Grid container spacing={3}>
              {unsecuredProducts.map((prod, index) =>
                renderProductCard(prod, index, unsecuredProducts.length)
              )}
            </Grid>
          </Box>

          {/* Category 2: SECURED */}
          <Box mb={2}>
            <Typography
              variant="subtitle2"
              fontWeight="700"
              color="#101828"
              fontSize="14px"
              sx={{ textTransform: "uppercase", letterSpacing: "0.5px", mb: 2 }}
            >
              SECURED
            </Typography>
            <Grid container spacing={3}>
              {securedProducts.map((prod, index) =>
                renderProductCard(prod, index, securedProducts.length)
              )}
            </Grid>
          </Box>
        </Box>
      </MDBox>
    </DashboardLayout>
  );
}

export default NewDashboard;
